import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Star, ArrowRight } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import PageTransition from "../components/PageTransition";
import api from "../lib/api";
import { lowestNightly } from "../lib/roomPricing";
import { useApp } from "../context/AppContext";

// Build a pin icon with a price label for visual scanning (Booking-style)
const buildPriceIcon = (priceLabel) => new L.DivIcon({
    className: "asis-pin-with-price",
    html: `<div style="position:relative; display:flex; flex-direction:column; align-items:center; filter:drop-shadow(0 4px 6px rgba(0,0,0,0.5))">
        <div style="background:#fff; color:#082c27; padding:3px 8px; font-family:'Jost',sans-serif; font-size:11px; font-weight:700; border:1.5px solid #d4af37; white-space:nowrap; margin-bottom:-3px; border-radius:2px;">${priceLabel}</div>
        <svg viewBox='0 0 32 44' width='28' height='38' xmlns='http://www.w3.org/2000/svg'>
            <path fill='#d4af37' stroke='#5b4716' stroke-width='1.4' d='M16 2C8.27 2 2 8.27 2 16c0 9.5 14 26 14 26s14-16.5 14-26C30 8.27 23.73 2 16 2z'/>
            <circle cx='16' cy='16' r='5.5' fill='#082c27'/>
        </svg>
    </div>`,
    iconSize: [60, 56],
    iconAnchor: [30, 54],
    popupAnchor: [0, -50],
});

const CITY_CONFIG = {
    Mecca: { center: [21.4225, 39.8262], zoom: 14, label: "Mekke-i Mükerreme", landmark: "Mescid-i Haram" },
    Medina: { center: [24.4672, 39.6111], zoom: 14, label: "Medine-i Münevvere", landmark: "Mescid-i Nebevi" },
};

const MapView = () => {
    const { formatPrice } = useApp();
    const [hotels, setHotels] = useState([]);
    const [city, setCity] = useState("Mecca");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/hotels")
            .then(({ data }) => setHotels(data))
            .catch(() => setHotels([]))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => hotels.filter((h) => h.city === city && h.latitude && h.longitude), [hotels, city]);
    const cfg = CITY_CONFIG[city];

    return (
        <PageTransition>
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
                <div className="text-center mb-8">
                    <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3 flex items-center justify-center gap-2">
                        <MapPin className="w-3.5 h-3.5" /> Harita Görünümü
                    </div>
                    <h1 className="font-serif text-5xl lg:text-6xl text-balance">Otellerimiz Haritada</h1>
                    <p className="mt-3 text-base text-muted_green max-w-xl mx-auto">
                        Mekke ve Medine'deki tüm otellerimizin Mescid-i Haram ve Mescid-i Nebevi'ye olan mesafelerini görün.
                    </p>
                </div>

                {/* City toggle */}
                <div className="flex flex-wrap justify-center gap-2 mb-6 px-2" data-testid="map-city-toggle">
                    {Object.entries(CITY_CONFIG).map(([key, c]) => (
                        <button
                            key={key}
                            onClick={() => setCity(key)}
                            className={`px-4 sm:px-6 py-3 text-[11px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-[0.18em] border font-semibold transition-all flex items-center gap-1.5 sm:gap-2 ${
                                city === key
                                    ? "bg-gold text-emerald_deep border-gold"
                                    : "bg-emerald_surface text-bone/85 border-border_green hover:border-gold"
                            }`}
                            data-testid={`map-city-${key}`}
                        >
                            <MapPin className="w-3.5 h-3.5" /> {c.label}
                            <span className="opacity-70 text-[10px]">
                                ({hotels.filter((h) => h.city === key && h.latitude && h.longitude).length})
                            </span>
                        </button>
                    ))}
                </div>

                {/* Map */}
                <div className="grid lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 h-[520px] border border-border_green overflow-hidden">
                        {loading ? (
                            <div className="w-full h-full bg-emerald_surface animate-pulse" />
                        ) : (
                            <MapContainer
                                key={city}
                                center={cfg.center}
                                zoom={cfg.zoom}
                                scrollWheelZoom={true}
                                className="w-full h-full"
                                style={{ background: "#0e453d" }}
                                data-testid="hotels-map"
                            >
                                <TileLayer
                                    attribution='Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics'
                                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                                    maxZoom={19}
                                />
                                {/* Labels overlay so street/landmark names stay readable on satellite */}
                                <TileLayer
                                    attribution=''
                                    url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png"
                                    subdomains="abcd"
                                    maxZoom={19}
                                    opacity={0.9}
                                />
                                {filtered.map((h) => {
                                    const price = lowestNightly(h);
                                    const label = price > 0 ? formatPrice(price) : "—";
                                    return (
                                        <Marker
                                            key={h.id}
                                            position={[h.latitude, h.longitude]}
                                            icon={buildPriceIcon(label)}
                                        >
                                        <Popup>
                                            <div style={{ minWidth: "180px" }}>
                                                <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "17px", fontWeight: 700, marginBottom: "4px", color: "#082c27" }}>
                                                    {h.name}
                                                </div>
                                                <div style={{ fontSize: "11px", color: "#6a6048", marginBottom: "6px" }}>
                                                    <MapPin style={{ width: "10px", height: "10px", display: "inline", marginRight: "4px" }} />
                                                    Harem'e {h.distance_to_haram}
                                                </div>
                                                {price > 0 && (
                                                    <div style={{ fontSize: "13px", color: "#b08a1f", fontWeight: 700, marginBottom: "6px" }}>
                                                        {formatPrice(price)}'den başlayan fiyatlarla / gece
                                                    </div>
                                                )}
                                                <a
                                                    href={`/hotels/${h.id}`}
                                                    style={{ display: "inline-block", padding: "5px 10px", background: "#b08a1f", color: "#fff", fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase", textDecoration: "none", fontWeight: 700 }}
                                                >
                                                    Detayları gör →
                                                </a>
                                            </div>
                                        </Popup>
                                    </Marker>
                                    );
                                })}
                            </MapContainer>
                        )}
                    </div>

                    {/* Sidebar — hotel list for selected city */}
                    <div className="space-y-3 lg:max-h-[520px] lg:overflow-y-auto pr-1" data-testid="map-hotel-list">
                        {filtered.length === 0 ? (
                            <div className="border border-dashed border-border_green p-8 text-center text-sm text-muted_green">
                                Bu şehirde haritalı otel bulunamadı.
                            </div>
                        ) : (
                            filtered.map((h, i) => {
                                const sidePrice = lowestNightly(h);
                                return (
                                <motion.div
                                    key={h.id}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.04 }}
                                    className="border border-border_green bg-emerald_surface p-4 hover:border-gold transition-colors group"
                                    data-testid={`map-hotel-${h.id}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                            <div className="font-serif text-lg group-hover:text-gold transition-colors">{h.name}</div>
                                            <div className="text-[11px] text-muted_green mt-1">Harem'e {h.distance_to_haram}</div>
                                            <div className="flex items-center gap-0.5 mt-1">
                                                {Array.from({ length: h.star_rating || 5 }).map((_, j) => (
                                                    <Star key={j} className="w-3 h-3 fill-gold text-gold" />
                                                ))}
                                            </div>
                                        </div>
                                        {sidePrice > 0 && (
                                            <div className="text-right flex-shrink-0">
                                                <div className="text-[9px] uppercase text-muted_green">başlayan / gece</div>
                                                <div className="text-gold font-serif text-lg leading-tight">{formatPrice(sidePrice)}</div>
                                            </div>
                                        )}
                                    </div>
                                    <Link
                                        to={`/hotels/${h.id}`}
                                        className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-gold hover:underline"
                                    >
                                        Detay <ArrowRight className="w-3 h-3" />
                                    </Link>
                                </motion.div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
        </PageTransition>
    );
};

export default MapView;
